// OFX 1.x (SGML) and 2.x (XML) parser - client-side only.
// Extracts STMTTRN entries with date, value, fitid, memo/name.

export interface OFXTransaction {
  fitid: string;
  date: string; // YYYY-MM-DD (assumed UTC-3 / Brasília)
  amount: number; // signed (negative = debit)
  type: string; // TRNTYPE
  memo: string;
  name: string;
}

export interface OFXParsedData {
  bankId?: string;
  accountId?: string;
  periodStart?: string;
  periodEnd?: string;
  transactions: OFXTransaction[];
}

// Parse OFX date (YYYYMMDD or YYYYMMDDHHMMSS[.XXX][TZ]) -> YYYY-MM-DD
function parseOfxDate(raw: string | undefined): string {
  if (!raw) return "";
  const s = raw.trim();
  if (s.length < 8) return "";
  const y = s.slice(0, 4);
  const m = s.slice(4, 6);
  const d = s.slice(6, 8);
  return `${y}-${m}-${d}`;
}

// Read SGML-style tag (no closing tag required) or XML-style tag
function getTag(block: string, tag: string): string | undefined {
  // XML: <TAG>value</TAG>
  const xmlRe = new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`, "i");
  const xmlMatch = block.match(xmlRe);
  if (xmlMatch) return xmlMatch[1].trim();
  // SGML: <TAG>value (ends at next < or newline)
  const sgmlRe = new RegExp(`<${tag}>([^<\\r\\n]*)`, "i");
  const sgmlMatch = block.match(sgmlRe);
  if (sgmlMatch) return sgmlMatch[1].trim();
  return undefined;
}

export function parseOFX(content: string): OFXParsedData {
  // Strip OFX 1.x SGML header (everything before <OFX>)
  const ofxStart = content.search(/<OFX>/i);
  const body = ofxStart >= 0 ? content.slice(ofxStart) : content;

  const result: OFXParsedData = { transactions: [] };

  result.bankId = getTag(body, "BANKID");
  result.accountId = getTag(body, "ACCTID");

  const dtStart = getTag(body, "DTSTART");
  const dtEnd = getTag(body, "DTEND");
  if (dtStart) result.periodStart = parseOfxDate(dtStart);
  if (dtEnd) result.periodEnd = parseOfxDate(dtEnd);

  // Find every <STMTTRN>...</STMTTRN> block (works for SGML too when </STMTTRN> closes)
  const txRe = /<STMTTRN>([\s\S]*?)<\/STMTTRN>/gi;
  let match: RegExpExecArray | null;
  while ((match = txRe.exec(body)) !== null) {
    const block = match[1];
    const fitid = getTag(block, "FITID") || "";
    const dtPosted = parseOfxDate(getTag(block, "DTPOSTED"));
    const trnAmtRaw = getTag(block, "TRNAMT") || "0";
    const amount = parseFloat(trnAmtRaw.replace(",", "."));
    const trnType = getTag(block, "TRNTYPE") || "";
    const memo = getTag(block, "MEMO") || "";
    const name = getTag(block, "NAME") || "";

    if (!fitid || !dtPosted || isNaN(amount)) continue;

    result.transactions.push({
      fitid,
      date: dtPosted,
      amount,
      type: trnType,
      memo,
      name,
    });
  }

  // Fallback for OFX 1.x where </STMTTRN> may be missing
  if (result.transactions.length === 0) {
    const looseRe = /<STMTTRN>([\s\S]*?)(?=<STMTTRN>|<\/BANKTRANLIST>|<\/CCSTMTRS>|<\/STMTRS>)/gi;
    let m: RegExpExecArray | null;
    while ((m = looseRe.exec(body)) !== null) {
      const block = m[1];
      const fitid = getTag(block, "FITID") || "";
      const dtPosted = parseOfxDate(getTag(block, "DTPOSTED"));
      const trnAmtRaw = getTag(block, "TRNAMT") || "0";
      const amount = parseFloat(trnAmtRaw.replace(",", "."));
      if (!fitid || !dtPosted || isNaN(amount)) continue;
      result.transactions.push({
        fitid,
        date: dtPosted,
        amount,
        type: getTag(block, "TRNTYPE") || "",
        memo: getTag(block, "MEMO") || "",
        name: getTag(block, "NAME") || "",
      });
    }
  }

  return result;
}
